import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreAnchorLink, { StoreAnchorProps } from "@store/Components/Store/StoreAnchorLink";
import Clickable, { ClickablePresentationProps } from "@store/MobiusOverrides/Clickable/Clickable";
import { MOBILE_HEADER_HEIGHT, SMALLEST_HEADER_HEIGHT } from "@store/Overrides/Widgets/Header/LogoLinksSearch";
import { gridGapStyles, linkStyles, transitionHoverStyles } from "@store/theme/common/mixins";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";
import styled from "styled-components";

const enum fields {
    showAnchorMenu = "showAnchorMenu",
}
type OwnProps = HasCategoryContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    categoriesDataView: props.category?.id ? getCategoriesDataView(state, { maxDepth: 1, startCategoryId: props.category?.id }) : undefined,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const Anchor = styled.span`
    position: absolute;
    left: 0;
    height: 0;
    opacity: 0;
    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [{ values: [-MOBILE_HEADER_HEIGHT - 10, null, null, -SMALLEST_HEADER_HEIGHT -10], properties: ["top"], unit: "px" }],
            mediaOptions.min,
        )}
`;

export interface SecondLevelCategoryDetailsSubCategoriesListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyPresentationProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    subCategoryImageItem?: InjectableCss;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    anchorsMenuContainer?: GridContainerProps;
    anchorsMenuTitle?: TypographyPresentationProps;
    anchorsMenuTitleItem?: GridItemProps;
    anchorsMenuLinkItem?: GridItemProps;
    anchorsMenuLink?: LinkProps;
}

export const secondLevelCategoryDetailsSubCategoriesListStyles: SecondLevelCategoryDetailsSubCategoriesListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 0,
        css: css`
            ${gridGapStyles([], ["2em"])}
            margin-bottom: 3em;
        `,
    },
    categoryName: {
        variant: "h2",
        css: css`
            position: relative;
            width: 100%;
            padding-bottom: 0.7em;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    categoryNameLink: {
        css: css`
            ${linkStyles("primary", "text", false)};
        `,
    },
    categoryItem: {
        width: 12,
    },
    innerContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(
                    ["24px", "32px", null, "36px", "54px"], 
                    ["24px", "32px", null, "44px", "64px"]
                )
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [12, 6, 4, 3, 3],
    },
    subCategoryNameLink: {
        css: css`
            ${linkStyles("text", "primary", false)}
            font-size: ${TYPOGRAPHY.size.largest};
            margin-top: 1.25em;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["0.3em", "0.4em", "0.6em", "0.75em", "1.25em"], properties: ["margin-top"] }],
                    mediaOptions.min,
                )}
        `,
    },
    subCategoryImageItem: {
        css: css`
            width: 100%;
            text-align: center;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            position: relative;
            width: 100%;
            padding: 0;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["55%", "74%"], properties: ["padding-top"] }],
                    mediaOptions.min,
                )}

            &::before {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: linear-gradient(180deg, rgba(48, 48, 48, 0) 0%, rgba(48, 48, 48, 0.85) 100%);
                z-index: 1;
            }

            img {
                position: absolute;
                top: 50%;
                width: auto;
                max-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }
        `,
        errorTypographyProps: {
            css: css`
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
            `,
        },
    },
    anchorsMenuContainer: {
        gap: 0,
        css: css`
            margin-bottom: 30px;
        `,
    },
    anchorsMenuTitleItem: {
        width: 12,
        css: css`
            margin-bottom: 20px;
        `,
    },
    anchorsMenuTitle: {
        weight: TYPOGRAPHY.weight.black,
        css: css`
            margin-bottom: 20px;
        `,
    },
    anchorsMenuLink: {
        css: css`
            ${transitionHoverStyles("color")}
            color: ${getColor("text.accent")};

            &:hover {
                text-decoration: underline;
            }
        `,
    },
    anchorsMenuLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 12px;
        `,
    },
};

const styles = secondLevelCategoryDetailsSubCategoriesListStyles;

const SecondLevelCategoryDetailsSubCategoriesList = ({
    categoriesDataView,
    loadCategories,
    category: mainCategory,
    fields: {showAnchorMenu},
}: Props) => {
    React.useEffect(() => {
        if (mainCategory && !categoriesDataView?.isLoading && !categoriesDataView?.value) {
            loadCategories({ maxDepth: 1, startCategoryId: mainCategory?.id });
        }
    }, [mainCategory]);

    if (!categoriesDataView || categoriesDataView?.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!mainCategory || !mainCategory.subCategoryIds || mainCategory.subCategoryIds.length === 0 || !categoriesDataView?.value) {
        return null;
    }

    const categories = categoriesDataView.value;

    const anchors: Array<StoreAnchorProps> = [];
    categories.filter(x => x.properties.isShopAll !== "true").forEach(x => {
        anchors.push({
            label: x.shortDescription,
            anchor: x.urlSegment,
        });
    });

    return (
        <>
            {showAnchorMenu && (
                <>
                    <GridContainer {...styles.anchorsMenuContainer}>
                        <GridItem {...styles.anchorsMenuTitleItem}>
                            <Typography {...styles.anchorsMenuTitle}>{translate("Categories")}</Typography>
                        </GridItem>
                        {anchors.map((link: StoreAnchorProps) => {
                            return (
                                <GridItem key={link.anchor} {...styles.anchorsMenuLinkItem}>
                                    <StoreAnchorLink  {...link} {...styles.anchorsMenuLink} />
                                </GridItem>
                            );
                        })}
                    </GridContainer>
                </>
            )}
            {categories.filter(x => x.properties.isShopAll !== "true").map(category => (
                <GridContainer  key={category.id.toString()} {...styles.container}>
                    <GridItem {...styles.categoryItem}>
                        <Typography {...styles.categoryName}>
                            <Anchor id={category.urlSegment} aria-hidden="true">anchor</Anchor>
                            <Link href={category.path} {...styles.categoryNameLink}>
                                {category.shortDescription}
                            </Link>
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.categoryItem}>
                        <GridContainer {...styles.innerContainer}>
                            {category.subCategoryIds?.map(subCategoryId => (
                                <SubCategoryLink key={subCategoryId} categoryId={subCategoryId} />
                            ))}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            ))}
        </>
    );
}

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

type SubCategoryLinkProps = ReturnType<typeof linkMapStateToProps>;

const SubCategoryLinkView = (props: SubCategoryLinkProps) => {
	const { category } = props;

    if (!category || category.properties.isShopAll === "true") {
        return null;
    }

    return (
        <GridItem {...styles.subCategoryNameLinkItem}>
            <StyledWrapper {...styles.subCategoryImageItem}>
                <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                    <LazyImage
                        src={category.smallImagePath || COMMON.defaultCategoryImagePath}
                        altText={category.imageAltText}
                        {...styles.subCategoryImage}
                    />
                </Clickable>
                <Link href={category.path} {...styles.subCategoryNameLink}>
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
        </GridItem>
    );
};

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(connect(mapStateToProps, mapDispatchToProps)(SecondLevelCategoryDetailsSubCategoriesList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showAnchorMenu,
                displayName: "Show Anchor Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
